import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardBody, Badge } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../lib/api';
import { SEO } from '../components/SEO';

const DEFAULT_HERO_IMAGES = [
  'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80',
];

interface PropertyListItem {
  id: string;
  type: string;
  purpose: string;
  title: string;
  price: number | string;
  area: number;
  rooms: number;
  bathrooms: number;
  city: string;
  district: string | null;
  coverImage: string | null;
  featured: boolean;
  reviewedAt?: string | null;
  adLicenseNumber?: string | null;
}

interface HomeStats {
  totalProperties: number;
  totalActiveAuctions: number;
  totalUsers: number;
  liveAuctionsNow: number;
  liveBiddersNow: number;
}

export function HomePage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const navigate = useNavigate();

  // 📊 إحصاءات حية للموقع — endpoint عام (لا يحتاج صلاحيات)
  const { data: stats } = useQuery({
    queryKey: ['home', 'stats-public'],
    queryFn: () =>
      api
        .get<ApiSuccess<HomeStats>>('/api/properties/stats/public')
        .catch(() => ({
          data: { totalProperties: 0, totalActiveAuctions: 0, totalUsers: 0, liveAuctionsNow: 0, liveBiddersNow: 0 },
        })),
    refetchInterval: 30_000, // يحدّث الأرقام كل 30 ثانية → إحساس "حي"
  });
  const liveCount = stats?.data?.liveAuctionsNow ?? 0;
  const upcomingCount = (stats?.data?.totalActiveAuctions ?? 0) - liveCount;
  const propertyCount = stats?.data?.totalProperties ?? 0;

  // 🖼️ صور Hero — قابلة للتخصيص من لوحة الإدارة (settings.hero_images)
  const { data: settingsRes } = useQuery({
    queryKey: ['public-settings-hero'],
    queryFn: () =>
      api
        .get<ApiSuccess<Record<string, string | null>>>('/api/settings')
        .catch(() => ({ data: {} as Record<string, string | null> })),
    staleTime: 60_000,
  });
  const heroImages = useMemo<string[]>(() => {
    const raw = settingsRes?.data?.hero_images;
    if (!raw) return DEFAULT_HERO_IMAGES;
    try {
      const arr = JSON.parse(raw) as string[];
      return Array.isArray(arr) && arr.length > 0 ? arr : DEFAULT_HERO_IMAGES;
    } catch {
      return DEFAULT_HERO_IMAGES;
    }
  }, [settingsRes?.data?.hero_images]);

  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 5000);
    return () => clearInterval(id);
  }, [heroImages.length]);

  // ✨ عدّاد متحرّك للأرقام (يضيف "حياة" للصفحة)
  const animatedProperties = useAnimatedNumber(propertyCount);
  const animatedLive = useAnimatedNumber(liveCount);

  // 🏘️ عقارات مميّزة (top 6)
  const { data: featuredData } = useQuery({
    queryKey: ['home', 'featured'],
    queryFn: () => api.get<Paginated<PropertyListItem>>('/api/properties', { query: { featured: true, limit: 6 } }),
  });
  const featuredProps = featuredData?.data ?? [];

  // 🔍 بحث سريع من الـ Hero
  const [quickSearch, setQuickSearch] = useState({ purpose: '', city: '', type: '' });
  const handleQuickSearch = () => {
    const params = new URLSearchParams();
    if (quickSearch.purpose) params.set('purpose', quickSearch.purpose);
    if (quickSearch.city) params.set('city', quickSearch.city);
    if (quickSearch.type) params.set('type', quickSearch.type);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div>
      <SEO
        title="منصة العقارات والمزادات الأولى في السعودية"
        description="منصة شركة صورة العقارية للعقارات والمزادات الإلكترونية في المملكة العربية السعودية. آلاف الفلل والشقق والأراضي للبيع والإيجار، مزادات مباشرة على الهواء، ومسوقون عقاريون معتمدون من الهيئة العامة للعقار (REGA)."
        keywords="عقارات السعودية, مزادات عقارية, عقارات الرياض, فلل للبيع, شقق للإيجار, أراضي للبيع, مزاد عقاري, مسوق عقاري, اتحاد الملاك, البورصة العقارية, الهيئة العامة للعقار, REGA, رخصة فال, صورة العقارية, شركة صورة العقارية, IMG, img.sa, استثمار عقاري, تمويل عقاري, منصة عقارية, موقع عقاري, تطبيق عقاري, حاسبة عقارية, مزاد على الهواء, مزاد مباشر, إعلانات عقارية مرخصة"
        canonical="https://img.sa/"
        jsonLd={{
          '@type': 'RealEstateAgent',
          name: 'شركة صورة العقارية',
          alternateName: 'Sora Real Estate Company',
          url: 'https://img.sa',
          logo: 'https://img.sa/logo.png',
          telephone: '+966-11-499-3666',
          email: 'info@img.sa',
          address: { '@type': 'PostalAddress', addressLocality: 'الرياض', addressCountry: 'SA' },
          areaServed: { '@type': 'Country', name: 'المملكة العربية السعودية' },
          priceRange: '$$',
        }}
      />
      {/* ━━━━━━━━━━━━━━━━━━━━ HERO — أزرق فاتح ناعم + كاروسيل ━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="relative overflow-hidden text-ink"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 15% 0%, rgba(201,162,79,0.08) 0%, transparent 55%),' +
            'radial-gradient(ellipse 70% 60% at 85% 100%, rgba(107,141,177,0.20) 0%, transparent 55%),' +
            'linear-gradient(135deg, #dde8f5 0%, #e9f0f9 35%, #eef4fb 65%, #dce6f3 100%)',
        }}
      >
        {/* لمسات إضاءة ناعمة */}
        <div className="absolute -right-32 -top-32 w-[480px] h-[480px] rounded-full bg-navy-light/15 blur-3xl animate-pulse-slow" />
        <div className="absolute -left-32 -bottom-32 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="max-w-7xl mx-auto px-4 py-14 md:py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* 📝 النص + شريط البحث */}
            <div>
              <span className="inline-flex items-center gap-2 bg-navy-soft text-navy-dark text-xs font-bold px-3 py-1.5 rounded-full mb-4 border border-navy-light/20">
                ⚡ {ar ? 'منصة عقارية متكاملة بتقنية متقدمة' : 'Integrated platform with advanced tech'}
              </span>
              <h1 className="text-4xl md:text-6xl font-black leading-tight mb-3 text-navy-darker">
                {ar ? (
                  <>العقار <span className="text-gold-dark">بصورة</span> جديدة</>
                ) : (
                  <>Real estate, in <span className="text-gold-dark">a new image</span></>
                )}
              </h1>
              <p className="text-ink-secondary text-base md:text-lg leading-relaxed mb-6 max-w-xl">
                {ar
                  ? 'من البيع والشراء والتأجير إلى المزادات الفورية، التطوير العقاري، اتحاد الملاك، وإدارة المرافق. تجربة استثنائية بقاعدة بيانات جبارة.'
                  : 'Buy, sell, rent, live auctions, real estate development, owners association, and facility management — all in one platform.'}
              </p>

              {/* 🔍 شريط البحث السريع */}
              <div className="bg-white rounded-2xl p-2 shadow-xl border border-gray-200 mb-6">
                <div className="flex flex-wrap gap-1.5">
                  <select
                    value={quickSearch.purpose}
                    onChange={(e) => setQuickSearch({ ...quickSearch, purpose: e.target.value })}
                    className="flex-1 min-w-[110px] px-3 py-3 text-ink font-bold text-sm bg-transparent border-none outline-none cursor-pointer"
                  >
                    <option value="">{ar ? 'الغرض' : 'Purpose'}</option>
                    <option value="SALE">{ar ? 'شراء' : 'Buy'}</option>
                    <option value="RENT">{ar ? 'إيجار' : 'Rent'}</option>
                    <option value="INVESTMENT">{ar ? 'استثمار' : 'Investment'}</option>
                  </select>
                  <select
                    value={quickSearch.city}
                    onChange={(e) => setQuickSearch({ ...quickSearch, city: e.target.value })}
                    className="flex-1 min-w-[110px] px-3 py-3 text-ink font-bold text-sm bg-transparent border-none outline-none cursor-pointer"
                  >
                    <option value="">{ar ? 'كل المدن' : 'All cities'}</option>
                    <option>الرياض</option><option>جدة</option><option>الدمام</option><option>مكة المكرمة</option><option>المدينة المنورة</option><option>الخبر</option>
                  </select>
                  <select
                    value={quickSearch.type}
                    onChange={(e) => setQuickSearch({ ...quickSearch, type: e.target.value })}
                    className="flex-1 min-w-[110px] px-3 py-3 text-ink font-bold text-sm bg-transparent border-none outline-none cursor-pointer"
                  >
                    <option value="">{ar ? 'نوع العقار' : 'Type'}</option>
                    <option value="VILLA">{ar ? 'فيلا' : 'Villa'}</option>
                    <option value="APARTMENT">{ar ? 'شقة' : 'Apartment'}</option>
                    <option value="LAND">{ar ? 'أرض' : 'Land'}</option>
                    <option value="COMMERCIAL">{ar ? 'تجاري' : 'Commercial'}</option>
                  </select>
                  <button
                    onClick={handleQuickSearch}
                    className="px-6 py-3 rounded-xl bg-gradient-to-l from-gold-dark to-gold hover:from-gold hover:to-gold-light text-white font-black text-sm shadow-md transition"
                  >
                    🔍 {ar ? 'ابحث' : 'Search'}
                  </button>
                </div>
              </div>

              {/* 🛡️ Trust signals — حقيقية (امتثال نظامي بدل أرقام وهمية) */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex -space-x-2">
                  <span className="w-10 h-10 rounded-full bg-emerald-50 border-2 border-white shadow-sm flex items-center justify-center text-emerald-700 text-lg">🛡️</span>
                  <span className="w-10 h-10 rounded-full bg-amber-50 border-2 border-white shadow-sm flex items-center justify-center text-amber-700 text-lg">⚖️</span>
                  <span className="w-10 h-10 rounded-full bg-navy-soft border-2 border-white shadow-sm flex items-center justify-center text-navy-dark text-lg">✓</span>
                </div>
                <div>
                  <div className="font-black text-navy-darker">{ar ? 'منصة معتمدة وفق أنظمة الهيئة العامة للعقار' : 'REGA-compliant platform'}</div>
                  <div className="text-ink-muted text-sm">{ar ? 'تحقق من رخصة فال ورقم الإعلان قبل النشر' : 'Verified Fal license & ad number before publishing'}</div>
                </div>
              </div>
            </div>

            {/* 🖼️ كاروسيل صور (يُتحكَّم به من لوحة الإدارة) + بطاقات عائمة */}
            <div className="relative hidden md:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative aspect-[4/3] max-h-[420px]">
                {heroImages.map((src, i) => (
                  <img
                    key={src + i}
                    src={src}
                    alt=""
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      i === heroIdx ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                {/* تظليل سفلي خفيف لتباين البطاقات العائمة */}
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

                {/* مؤشرات النقاط — يقدر يفتح الصورة المطلوبة */}
                {heroImages.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {heroImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setHeroIdx(i)}
                        aria-label={`صورة ${i + 1}`}
                        className={`h-1.5 rounded-full transition-all ${
                          i === heroIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* بطاقة المزاد المباشر */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 w-56 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center text-xl">🔨</div>
                  <div>
                    <div className="text-[10px] text-ink-muted">{ar ? 'المزاد المباشر' : 'Live Auction'}</div>
                    <div className="font-black text-navy-dark text-sm">
                      <span className="tabular-nums">{animatedLive}</span> {ar ? (liveCount === 1 ? 'مزاد نشط' : 'مزاد نشط') : 'active'}
                    </div>
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-red-600 font-black">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  LIVE
                </div>
              </div>

              {/* بطاقة العقارات — رقم حقيقي من الـ API + عداد متحرك */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 w-60 animate-float" style={{ animationDelay: '1s' }}>
                <div className="text-[10px] text-ink-muted">{ar ? 'العقارات المعتمدة' : 'Verified properties'}</div>
                <div className="font-black text-3xl text-navy-dark tabular-nums">
                  {animatedProperties.toLocaleString('en-US')}
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-l from-gold to-navy transition-all duration-1000"
                    style={{ width: propertyCount > 0 ? '100%' : '0%' }}
                  />
                </div>
                <Link
                  to="/properties"
                  className="text-[10px] text-navy-dark font-bold mt-1 hover:text-gold-dark transition inline-block"
                >
                  ✓ {ar ? 'تصفّح القائمة الكاملة' : 'Browse all'} ←
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 🛡️ COMPLIANCE BANNER ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="bg-gradient-to-l from-green-50 via-white to-green-50 border-y border-green-100 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <span className="inline-block bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">🛡️ {ar ? 'موثوقة' : 'Verified'}</span>
            <h2 className="text-2xl md:text-3xl font-black text-navy-dark mt-2">
              {ar ? 'منصة معتمدة وفق أنظمة الهيئة العامة للعقار' : 'Compliant with REGA Regulations'}
            </h2>
            <p className="text-sm text-ink-secondary mt-1">
              {ar ? 'كل إعلان يُراجع يدوياً ونتحقق من رخصة فال ورقم الترخيص قبل النشر' : 'Every listing is manually verified'}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { i: '🪪', t: 'رخصة فال', s: 'للمعلنين فقط' },
              { i: '📜', t: 'رخصة الإعلان', s: 'من الهيئة العامة' },
              { i: '⚖️', t: 'تحقق من الصك', s: 'وزارة العدل' },
              { i: '✓', t: 'مراجعة يدوية', s: '5 خطوات تحقق' },
            ].map((b) => (
              <div key={b.t} className="text-center p-3 bg-white rounded-xl border border-gray-100">
                <div className="text-3xl mb-1">{b.i}</div>
                <div className="text-xs font-black text-navy-dark">{b.t}</div>
                <div className="text-[10px] text-ink-muted">{b.s}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-5">
            <Link to="/compliance" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-navy-dark hover:bg-navy text-white text-sm font-bold transition">
              🛡️ {ar ? 'اعرف المزيد عن التزامنا' : 'Learn more'}
            </Link>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 🛠 SERVICES (8 خدمات) ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-2">💼 {ar ? 'خدماتنا' : 'Services'}</span>
          <h2 className="text-3xl md:text-4xl font-black text-navy-dark">{ar ? 'باقة خدمات عقارية شاملة' : 'Complete Real Estate Suite'}</h2>
          <p className="text-ink-secondary mt-2 max-w-2xl mx-auto">
            {ar ? 'من اللحظة اللي تفكر فيها بالعقار، إلى آخر تفاصيل إدارته — نحن معك في كل خطوة' : 'From idea to management — we are with you every step'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ServiceCard icon="🔑" title="شراء وبيع"      desc="عقارات موثّقة برخصة إعلان معتمدة" link="/properties" />
          <ServiceCard icon="🏡" title="التأجير العقاري" desc="عقود إيجار موثّقة بنظام إيجار" link="/properties?purpose=RENT" />
          <ServiceCard icon="🔨" title="المزادات الحية"  desc="شفافية كاملة، مزايدات لحظية" link="/auctions" highlight live />
          <ServiceCard icon="🏛️" title="اتحاد الملاك"   desc="إدارة احترافية وفق نظام الهيئة" link="/mullak" />
          <ServiceCard icon="🔧" title="إدارة المرافق"  desc="صيانة، نظافة، أمن وحراسة" link="/services" />
          <ServiceCard icon="🏗️" title="التطوير العقاري" desc="من الفكرة حتى التسليم" link="/design" />
          <ServiceCard icon="⚖️" title="التقييم العقاري" desc="تقييم عادل ومعتمد من خبراء السوق" link="/services" />
          <ServiceCard icon="✨" title="جميع الخدمات"   desc="استعرض جميع خدماتنا بالتفصيل" link="/services" gold />
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 🏘️ FEATURED PROPERTIES ━━━━━━━━━━━━━━━━━━━━ */}
      {featuredProps.length > 0 && (
        <section className="bg-gradient-to-b from-navy-soft/30 to-white py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-1">⭐ {ar ? 'العروض المميّزة' : 'Featured'}</span>
                <h2 className="text-3xl font-black text-navy-dark">{ar ? 'عقارات مختارة لك' : 'Selected for you'}</h2>
                <p className="text-ink-secondary text-sm mt-1">{ar ? 'تصفّح أبرز العروض المختارة من فريقنا أو شاهد القائمة الكاملة' : 'Browse top picks or view all listings'}</p>
              </div>
              <Link to="/properties">
                <Button variant="ghost">{ar ? 'جميع العقارات' : 'All listings'} ←</Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredProps.map((p) => <FeaturedPropertyCard key={p.id} property={p} ar={ar} />)}
            </div>

            <div className="text-center mt-8">
              <Link to="/properties">
                <Button variant="primary" size="lg">🏘️ {ar ? 'تصفّح كل العقارات' : 'Browse all'}</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━ 3 أقسام موحّدة الإيقاع: Live Auctions → Smart Tools → CTA ━━━━━━━━━━━━━━━━━━━━ */}
      {/* كل قسم: نفس المسافات، نفس عرض الكرت، نفس بنية العناوين (Eyebrow → Title → Body → Action) */}

      {/* 1️⃣ المزادات الحية — كرت بتدرّج navy فاتح */}
      <section className="px-4 pt-12 pb-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl p-8 md:p-10 text-white shadow-lg"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-white/90 text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></span>
                  {ar ? 'مباشر الآن' : 'LIVE'}
                </span>
                <h2 className="text-2xl md:text-3xl font-black mb-2 leading-tight">
                  {ar ? 'منصة المزادات العقارية الحية' : 'Live Real Estate Auctions'}
                </h2>
                <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-xl">
                  {ar
                    ? 'شفافية كاملة، مزايدات لحظية، حضور موثّق، شات حي، بث HD، وإثبات إلكتروني معتمد.'
                    : 'Full transparency, real-time bidding, certified attendance, live HD streaming.'}
                </p>
                <Link to="/auctions">
                  <Button variant="gold" size="lg">{ar ? 'دخول المنصة' : 'Enter'} ←</Button>
                </Link>
              </div>

              {/* أرقام حقيقية في عمود مرتّب */}
              <div className="grid grid-cols-3 gap-3 md:gap-2 md:border-s md:border-white/10 md:ps-8">
                <CompactStat value={animatedLive} label={ar ? 'مباشر' : 'Live'} accent="text-red-300" />
                <CompactStat value={stats?.data?.liveBiddersNow ?? 0} label={ar ? 'مزايد' : 'Bidders'} accent="text-gold-light" />
                <CompactStat value={upcomingCount > 0 ? upcomingCount : 0} label={ar ? 'قادمة' : 'Upcoming'} accent="text-navy-light" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2️⃣ الأدوات الذكية — كرت فاتح بنفس الإيقاع */}
      <section className="px-4 py-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl p-8 md:p-10 bg-gradient-to-br from-navy-soft/60 via-white to-gold-soft/40 border border-navy-soft shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <span className="inline-block bg-gold-soft text-gold-dark text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                  {ar ? 'الأدوات الذكية' : 'Smart Tools'}
                </span>
                <h2 className="text-2xl md:text-3xl font-black text-navy-darker mb-2 leading-tight">
                  {ar ? 'أدوات ذكية تساعدك في القرار' : 'Smart tools to make confident decisions'}
                </h2>
                <p className="text-ink-secondary text-sm leading-relaxed mb-5 max-w-xl">
                  {ar
                    ? 'تمويل عقاري، زكاة، عائد استثماري، رسوم وتكاليف، تكلفة البناء، ورسوم نقل الملكية.'
                    : 'Mortgage, Zakat, ROI, transfer fees, construction cost, and more.'}
                </p>
                <Link to="/calculators">
                  <Button variant="primary" size="lg">{ar ? 'افتح الأدوات' : 'Open Tools'} ←</Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <ToolCard icon="🏦" label="التمويل العقاري" />
                <ToolCard icon="💰" label="الزكاة" />
                <ToolCard icon="📈" label="العائد الاستثماري" />
                <ToolCard icon="🏗️" label="تكلفة البناء" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3️⃣ CTA — كرت بتدرّج navy فاتح (نفس Section 1 لإغلاق الإيقاع) */}
      <section className="px-4 pt-6 pb-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl p-8 md:p-10 text-white shadow-lg"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-black mb-2 leading-tight">
                  {ar ? 'جاهز تبدأ رحلتك العقارية معنا؟' : 'Ready to start?'}
                </h2>
                <p className="text-white/75 text-sm md:text-base">
                  {ar
                    ? 'سواء كنت بائع، مشتري، مستثمر، أو مالك مبنى — عندنا الحل المناسب.'
                    : 'Buyer, seller, investor, or owner — we have you covered.'}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap md:justify-end">
                <Link to="/properties">
                  <Button variant="gold" size="lg">{ar ? 'تصفّح العقارات' : 'Browse'}</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" size="lg" className="!bg-white !text-navy-dark !border-white">
                    {ar ? 'تواصل معنا' : 'Contact'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Sub Components ━━━━━━━━━━━━━━━━━━━━

function ServiceCard({ icon, title, desc, link, highlight, gold, live }: {
  icon: string; title: string; desc: string; link: string; highlight?: boolean; gold?: boolean; live?: boolean;
}) {
  const cls = gold
    ? 'bg-gradient-to-br from-navy to-navy-dark text-white'
    : highlight
    ? 'bg-gradient-to-br from-navy-darker to-navy-dark text-white'
    : 'bg-gradient-to-br from-white to-navy-soft/30 text-ink border border-gray-100';
  return (
    <Link to={link} className={`${cls} block p-6 rounded-2xl hover:shadow-brand-lg transition-all hover:-translate-y-1 relative overflow-hidden`}>
      {live && (
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[9px] font-black text-red-400">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
          LIVE
        </span>
      )}
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-3 ${
        gold || highlight ? 'bg-gold/20 text-gold' : 'bg-navy-soft text-navy-dark'
      }`}>{icon}</div>
      <h3 className="font-black text-base mb-1">{title}</h3>
      <p className={`text-xs ${gold || highlight ? 'text-white/70' : 'text-ink-muted'}`}>{desc}</p>
    </Link>
  );
}

function FeaturedPropertyCard({ property: p, ar }: { property: PropertyListItem; ar: boolean }) {
  return (
    <Link to={`/properties/${p.id}`} className="group">
      <Card hover className="h-full overflow-hidden">
        <div className="h-48 bg-gradient-to-br from-navy to-navy-dark relative">
          {p.coverImage ? (
            <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-5xl">🏠</div>
          )}
          <div className="absolute top-3 right-3 flex gap-1">
            {p.featured && <Badge color="gold">⭐ مميّز</Badge>}
            <Badge color={p.purpose === 'SALE' ? 'gold' : p.purpose === 'INVESTMENT' ? 'green' : 'navy'}>
              {p.purpose === 'SALE' ? (ar ? 'بيع' : 'Sale') : p.purpose === 'INVESTMENT' ? (ar ? 'استثمار' : 'Invest') : (ar ? 'إيجار' : 'Rent')}
            </Badge>
          </div>
          {p.adLicenseNumber && (
            <div className="absolute bottom-3 right-3">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold text-green-700 border border-green-200 shadow-sm">
                ✓ معتمد
              </span>
            </div>
          )}
        </div>
        <CardBody>
          <h3 className="font-black text-ink line-clamp-1 mb-1">{p.title}</h3>
          <p className="text-xs text-ink-muted mb-3">📍 {p.city}{p.district ? ` · ${p.district}` : ''}</p>
          <div className="flex items-center justify-between">
            <div className="text-navy-dark font-black text-lg">
              {Number(p.price).toLocaleString('en-US')} <span className="text-xs text-ink-muted">ر.س</span>
            </div>
            <div className="flex gap-2 text-xs text-ink-muted">
              <span>📐 {p.area}</span>
              <span>🛏 {p.rooms}</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

function StatBox({ value, label, hint }: { value: number | string; label: string; hint: string }) {
  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
      <div className="text-4xl font-black text-gold">{value}</div>
      <div className="font-bold mt-1">{label}</div>
      <div className="text-white/50 text-xs mt-1">{hint}</div>
    </div>
  );
}

/* عدّاد مدمج للقسم الأنيق (بدل StatBox الكبير) */
function CompactStat({ value, label, accent = 'text-gold-light' }: { value: number | string; label: string; accent?: string }) {
  return (
    <div className="text-center md:text-start">
      <div className={`text-3xl md:text-4xl font-black tabular-nums ${accent}`}>{value}</div>
      <div className="text-white/60 text-xs md:text-sm mt-1">{label}</div>
    </div>
  );
}

function ToolCard({ icon, label }: { icon: string; label: string }) {
  return (
    <Link to="/calculators" className="text-center bg-white border border-gray-100 rounded-xl p-4 hover:border-gold hover:shadow-brand transition hover:-translate-y-0.5">
      <div className="text-3xl mb-1">{icon}</div>
      <div className="text-xs font-bold text-navy-dark">{label}</div>
    </Link>
  );
}

/* 🔢 Hook لعدّ تصاعدي ناعم — يعطي إحساس "حياة" للأرقام */
function useAnimatedNumber(target: number, durationMs = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target <= 0) {
      setValue(0);
      return;
    }
    const start = performance.now();
    const from = 0;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

